import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const PREVIEW_MAX_HEIGHT = 220;

const PANEL_WIDTH = 320;

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) {
    return "";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 100 ? 0 : size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString();
  } catch (error) {
    return String(value);
  }
};

const AssetDetailsContent = ({ asset }) => {
  if (!asset) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select an asset to view its details.
      </Typography>
    );
  }

  const previewType = (() => {
    const type = asset?.contentType || "";

    if (type.startsWith("image/")) {
      return "image";
    }

    if (type.startsWith("video/")) {
      return "video";
    }

    if (type.startsWith("audio/")) {
      return "audio";
    }

    return "generic";
  })();

  const handleOpenAsset = () => {
    if (!asset?.url) {
      return;
    }

    window.open(asset.url, "_blank", "noopener");
  };

  const handleCopyUrl = async () => {
    if (!asset?.url) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(asset.url);
      } else if (typeof document !== "undefined") {
        const textArea = document.createElement("textarea");
        textArea.value = asset.url;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error("Failed to copy asset URL", error);
    }
  };

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      <Box
        sx={{
          position: "relative",
          height: PREVIEW_MAX_HEIGHT,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {previewType === "image" && asset?.url ? (
          <Box
            component="img"
            src={asset.url}
            alt={asset?.metadata?.alt || asset?.title || asset?.fileName}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : previewType === "video" && asset?.url ? (
          <Box
            component="video"
            src={asset.url}
            muted
            loop
            playsInline
            controls
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : previewType === "audio" ? (
          <Typography variant="body1" color="text.secondary">
            Audio asset
          </Typography>
        ) : (
          <Typography variant="body1" color="text.secondary">
            {asset?.contentType || "Asset"}
          </Typography>
        )}
      </Box>

      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {asset?.title || asset?.fileName || "Untitled asset"}
          </Typography>
          {asset?.status && (
            <Chip
              label={asset.status}
              size="small"
              color={asset.status === "READY" ? "success" : "default"}
            />
          )}
        </Stack>
        {asset?.description && (
          <Typography variant="body2" color="text.secondary">
            {asset.description}
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            startIcon={<LaunchIcon fontSize="small" />}
            onClick={handleOpenAsset}
            disabled={!asset?.url}
          >
            Open asset
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={handleCopyUrl}
            disabled={!asset?.url}
          >
            Copy URL
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={1.5}>
        <Typography variant="subtitle2" color="text.secondary">
          Details
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            File name
          </Typography>
          <Typography variant="body1">{asset?.fileName || "—"}</Typography>
        </Stack>
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            Content type
          </Typography>
          <Typography variant="body1">{asset?.contentType || "—"}</Typography>
        </Stack>
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            Size
          </Typography>
          <Typography variant="body1">
            {asset?.size ? formatFileSize(asset.size) : "—"}
          </Typography>
        </Stack>
        {(asset?.createdAt || asset?.updatedAt) && (
          <Stack spacing={1}>
            {asset?.createdAt && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(asset.createdAt)}
                </Typography>
              </Stack>
            )}
            {asset?.updatedAt && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Updated
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(asset.updatedAt)}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}

        {Array.isArray(asset?.tags) && asset.tags.length > 0 && (
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Tags
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {asset.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

const AuthoringHelpPanel = ({ open, onClose, currentType, content }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const highlights = currentType?.highlights ?? [];
  const isAssetDetails = content?.type === "asset";
  const asset = isAssetDetails ? content?.asset : null;

  const panelContent = (
    <Box
      role="complementary"
      aria-label={isAssetDetails ? "Asset details" : "Design checklist"}
      sx={{
        width: { xs: "100%", md: PANEL_WIDTH },
        boxSizing: "border-box",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {isAssetDetails ? (
              <InfoOutlinedIcon color="primary" fontSize="small" />
            ) : (
              <HelpOutlineIcon color="primary" fontSize="small" />
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {isAssetDetails ? "Asset details" : "Design checklist"}
            </Typography>
          </Stack>
          <IconButton
            aria-label={
              isAssetDetails ? "Close asset details" : "Close design checklist"
            }
            size="small"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {isAssetDetails ? (
          <AssetDetailsContent asset={asset} />
        ) : (
          <List dense sx={{ p: 0, m: 0 }}>
            {highlights.map((highlight) => (
              <ListItem key={highlight} sx={{ alignItems: "flex-start", px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                  <CheckCircleOutlineIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={highlight} />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  if (isDesktop) {
    return (
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          flexShrink: 0,
          width: open ? PANEL_WIDTH : 0,
          transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.shortest,
          }),
          borderLeft: open ? 1 : 0,
          borderColor: "divider",
          overflow: "hidden",
        }}
        aria-hidden={!open}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            position: "sticky",
            top: 0,
            height: "100vh",
            bgcolor: "background.paper",
          }}
        >
          {panelContent}
        </Box>
      </Box>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: "block", md: "none" },
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: 360,
        },
      }}
    >
      {panelContent}
    </Drawer>
  );
};

export default AuthoringHelpPanel;
